import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Button, Theme, Typography, makeStyles } from '@material-ui/core'
import CheckIcon from '@material-ui/icons/Check'
import PriorityHighIcon from '@material-ui/icons/PriorityHigh'
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked'
import {
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineSeparator,
} from '@material-ui/lab'
import clsx from 'clsx'
import React from 'react'

const useStyle = makeStyles((theme: Theme) => ({
    selectionWrapper: {
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    elementText: {
        fontSize: pxToRem(14),
        fontWeight: 500,
    },
    elementTextPoints: {
        color: '#676b6f',
        marginLeft: theme.spacing(0.5),
    },
    selectionButtons: {
        display: 'flex',
        '& .roundButtonContainer': {
            padding: theme.spacing(0, 1),
        },
    },
    buttonsHidden: {
        visibility: 'hidden',
    },
    elementSelectionsItem: {
        '&:before': {
            flex: theme.spacing(0),
            padding: theme.spacing(0),
        },
    },
    elementSelectionsStatus: {
        padding: theme.spacing(0.3),
        marginTop: theme.spacing(0),
        marginBottom: theme.spacing(0),
        boxShadow: 'none',
    },
    elementSelectionsStatusYes: {
        backgroundColor: '#0B782D',
    },
    elementSelectionsStatusNo: {
        color: '#B50808',
        border: '4px solid #B50808',
    },
    elementSelectionsStatusIconNo: {
        color: '#fff',
        backgroundColor: '#B50808',
    },
    elementSelectionsStatusNone: {
        color: '#677b98',
        backgroundColor: '#677b98',
    },
    elementSelectionsIcon: {
        fontSize: pxToRem(15),
    },
    elementSelectionsIconNo: {
        fontSize: pxToRem(10),
    },
    elementSelectionsContent: {
        marginTop: theme.spacing(-0.25),
        paddingTop: theme.spacing(0),
    },
    elementSelectionsPrerequisiteContent: {
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
    },
    timelineItemRoot: {
        minHeight: theme.spacing(7.5),
    },
    btnText: {
        fontSize: pxToRem(14),
    },
    selectionBtn: {
        minWidth: theme.spacing(6),
        padding: theme.spacing(0.25, 0.75),
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        borderRadius: theme.spacing(6.25),
    },
    selectionBtnSelected: {
        color: '#1379B2',
        borderColor: '#1379B2',
    },
}))
export interface ImportGuideSelectionProps {
    fileKey?: string
    label?: string
    type?: string
    text?: string
    points?: number
    value?: boolean | number | null
    position?: string
    isSelectedFileExisting?: boolean
    handleGuideSelectionClick?: (
        fileKey: string,
        label: string,
        value: boolean,
        points: number
    ) => void
}
const ImportGuideSelection: React.FC<ImportGuideSelectionProps> = ({
    fileKey,
    label,
    type,
    text,
    points,
    value,
    position,
    isSelectedFileExisting,
    handleGuideSelectionClick,
}) => {
    const classes = useStyle()
    const statusIconUI = React.useMemo(() => {
        const newValue = type === 'prerequisite' ? !value : value
        if (newValue === null) {
            return (
                <TimelineDot
                    className={clsx(
                        classes.elementSelectionsStatus,
                        classes.elementSelectionsStatusNone
                    )}
                    data-testid="statusIcon"
                >
                    <RadioButtonUncheckedIcon
                        className={classes.elementSelectionsIcon}
                    />
                </TimelineDot>
            )
        } else if (newValue) {
            return (
                <TimelineDot
                    className={clsx(
                        classes.elementSelectionsStatus,
                        classes.elementSelectionsStatusYes
                    )}
                    data-testid="statusIcon"
                >
                    <CheckIcon className={classes.elementSelectionsIcon} />
                </TimelineDot>
            )
        } else {
            return (
                <TimelineDot
                    className={clsx(
                        classes.elementSelectionsStatus,
                        classes.elementSelectionsStatusNo
                    )}
                    variant="outlined"
                    data-testid="statusIcon"
                >
                    <PriorityHighIcon
                        className={clsx(
                            classes.elementSelectionsIcon,
                            classes.elementSelectionsIconNo
                        )}
                    />
                </TimelineDot>
            )
        }
    }, [type, value])

    const handleGuideSelectionButtonClick = React.useCallback(
        (fileKey, label, value, points) => {
            handleGuideSelectionClick?.(fileKey, label, value, points)
        },
        [handleGuideSelectionClick]
    )
    return (
        <>
            <TimelineItem
                className={classes.elementSelectionsItem}
                classes={{
                    root: classes.timelineItemRoot,
                }}
            >
                <TimelineSeparator>
                    {statusIconUI}
                    {(position === 'first' || position === 'middle') && (
                        <TimelineConnector />
                    )}
                </TimelineSeparator>
                <TimelineContent className={classes.elementSelectionsContent}>
                    <GridContainer className={classes.selectionWrapper}>
                        <GridItem>
                            <Typography className={classes.elementText}>
                                {text}
                                <span className={classes.elementTextPoints}>
                                    ({points} points)
                                </span>
                            </Typography>
                        </GridItem>
                        <GridItem
                            className={clsx(
                                classes.selectionButtons,
                                type === 'prerequisite' && classes.buttonsHidden
                            )}
                            data-testid="selectionButtons"
                        >
                            <Button
                                classes={{
                                    root: clsx(
                                        classes.selectionBtn,
                                        value === true &&
                                            classes.selectionBtnSelected
                                    ),
                                }}
                                variant="outlined"
                                data-testid="yesButton"
                                disabled={!isSelectedFileExisting}
                                onClick={() =>
                                    handleGuideSelectionButtonClick(
                                        fileKey as string,
                                        label as string,
                                        true,
                                        points as number
                                    )
                                }
                            >
                                <Typography className={classes.btnText}>
                                    Yes
                                </Typography>
                            </Button>
                            <Button
                                classes={{
                                    root: clsx(
                                        classes.selectionBtn,
                                        value === false &&
                                            classes.selectionBtnSelected
                                    ),
                                }}
                                variant="outlined"
                                data-testid="noButton"
                                disabled={!isSelectedFileExisting}
                                onClick={() =>
                                    handleGuideSelectionButtonClick(
                                        fileKey as string,
                                        label as string,
                                        false,
                                        points as number
                                    )
                                }
                            >
                                <Typography className={classes.btnText}>
                                    No
                                </Typography>
                            </Button>
                        </GridItem>
                    </GridContainer>
                </TimelineContent>
            </TimelineItem>
        </>
    )
}

export default ImportGuideSelection
